import { type FC, useMemo } from 'react';
import NumberBall from '@/components/base/NumberBall';
import FilterBadge, { EmptyFilterState } from './FilterBadge';
import { useStatisticsData } from '@/pages/statistics/StatisticsDataContext';

const HotColdNumbers: FC = () => {
  const { hotNumbers, coldNumbers, hotCombination, coldCombination, filteredDraws, loading, advancedFilters, resetFilters } = useStatisticsData();
  const totalDraws = filteredDraws.length || 200;

  const filterKey = useMemo(() => {
    return `${advancedFilters.numberRanges.join(',')}|${advancedFilters.dayOfWeeks.join(',')}|${advancedFilters.prizeRange}`;
  }, [advancedFilters]);

  if (loading) {
    return (
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {[1, 2].map((i) => (
          <div key={i} className="bg-background-100/80 border border-background-200/60 rounded-2xl p-6 animate-pulse">
            <div className="h-5 bg-background-200 rounded w-28 mb-5"></div>
            <div className="flex flex-wrap gap-3 mb-6">
              {[1, 2, 3, 4, 5, 6].map((j) => (
                <div key={j} className="w-10 h-10 rounded-full bg-background-200"></div>
              ))}
            </div>
            <div className="h-16 bg-background-200 rounded-xl"></div>
          </div>
        ))}
      </div>
    );
  }

  if (filteredDraws.length === 0) {
    return (
      <div>
        <div className="mb-4">
          <FilterBadge advancedFilters={advancedFilters} onReset={resetFilters} />
        </div>
        <EmptyFilterState />
      </div>
    );
  }

  return (
    <div>
      <div className="mb-4">
        <FilterBadge advancedFilters={advancedFilters} onReset={resetFilters} />
      </div>
      <div key={filterKey} className="grid grid-cols-1 lg:grid-cols-2 gap-6 animate-[contentFadeIn_0.35s_ease-out]">
        <div className="bg-background-100/80 border border-background-200/60 rounded-2xl p-6">
          <div className="flex items-center gap-2 mb-5">
            <div className="w-8 h-8 rounded-lg bg-accent-500 flex items-center justify-center">
              <i className="ri-fire-line text-accent-950 text-sm"></i>
            </div>
            <h3 className="font-heading text-lg text-foreground-900">핫 넘버 TOP 6</h3>
          </div>
          <p className="text-xs text-foreground-500 mb-5">
            최근 {totalDraws}회차 기준 가장 많이 출현한 번호입니다.
          </p>
          <div className="flex flex-wrap gap-3 mb-6">
            {hotNumbers.map((num, idx) => (
              <div key={num} className="flex flex-col items-center gap-1">
                <NumberBall number={num} size="md" animated animationDelay={idx * 80} />
                <span className="text-[10px] text-foreground-500">{idx + 1}위</span>
              </div>
            ))}
          </div>
          <div className="bg-secondary-50/60 border border-secondary-200/40 rounded-xl p-4">
            <p className="text-xs text-secondary-700 font-medium mb-3">
              <i className="ri-sparkling-line mr-1"></i>
              핫 넘버 추천 조합
            </p>
            <div className="flex flex-wrap items-center gap-2">
              {hotCombination.numbers.map((num, idx) => (
                <NumberBall key={num} number={num} size="sm" animated animationDelay={idx * 60} />
              ))}
              <span className="text-xs text-foreground-300 mx-1">+</span>
              <NumberBall number={hotCombination.bonus} size="sm" isBonus animated animationDelay={360} />
            </div>
          </div>
        </div>

        <div className="bg-background-100/80 border border-background-200/60 rounded-2xl p-6">
          <div className="flex items-center gap-2 mb-5">
            <div className="w-8 h-8 rounded-lg bg-secondary-500 flex items-center justify-center">
              <i className="ri-snowy-line text-white text-sm"></i>
            </div>
            <h3 className="font-heading text-lg text-foreground-900">콜드 넘버 BOTTOM 6</h3>
          </div>
          <p className="text-xs text-foreground-500 mb-5">
            최근 {totalDraws}회차 기준 가장 적게 출현한 번호입니다. &ldquo;언젠간 나오겠지&rdquo; 역발상 전략!
          </p>
          <div className="flex flex-wrap gap-3 mb-6">
            {coldNumbers.map((num, idx) => (
              <div key={num} className="flex flex-col items-center gap-1">
                <NumberBall number={num} size="md" animated animationDelay={idx * 80} />
                <span className="text-[10px] text-foreground-500">{idx + 1}위</span>
              </div>
            ))}
          </div>
          <div className="bg-secondary-50/60 border border-secondary-200/40 rounded-xl p-4">
            <p className="text-xs text-secondary-700 font-medium mb-3">
              <i className="ri-lightbulb-line mr-1"></i>
              콜드 넘버 역발상 조합
            </p>
            <div className="flex flex-wrap items-center gap-2">
              {coldCombination.numbers.map((num, idx) => (
                <NumberBall key={num} number={num} size="sm" animated animationDelay={idx * 60} />
              ))}
              <span className="text-xs text-foreground-300 mx-1">+</span>
              <NumberBall number={coldCombination.bonus} size="sm" isBonus animated animationDelay={360} />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default HotColdNumbers;